import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, MapPin, Clock, ArrowDown } from "lucide-react";
import { useT } from "../i18n";
import { scrollToId } from "../lib/lenis";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: "0%" },
  transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  const t = useT();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  const lines = [t.hero_line_1, t.hero_line_2, t.hero_line_3];

  return (
    <section id="hero" ref={ref} className="relative min-h-[82vh] flex items-center overflow-hidden bg-bergamote-50" data-testid="hero-section">
      <motion.span
        style={{ y: bgY }}
        aria-hidden
        className="text-outline font-serif font-bold absolute -bottom-8 left-0 text-[22vw] leading-none whitespace-nowrap select-none pointer-events-none"
      >
        BERGAMOTE
      </motion.span>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-16 grid lg:grid-cols-12 gap-12 items-center relative z-10 w-full">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="font-mono text-xs uppercase tracking-[0.35em] text-bergamote-600 mb-6"
            data-testid="hero-eyebrow"
          >
            {t.hero_eyebrow}
          </motion.p>

          <h1 className="font-serif font-semibold tracking-tight leading-[1.05] text-4xl sm:text-6xl lg:text-7xl text-bergamote-900" data-testid="hero-title">
            {lines.map((line, i) => (
              <span key={`${line}-${i}`} className="block overflow-hidden pb-1">
                <motion.span className="block" {...lineAnim(i)}>
                  {i === 1 ? (
                    <span className="italic text-bergamote-600">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-6 text-base sm:text-lg text-bergamote-800 max-w-xl"
            data-testid="hero-subtitle"
          >
            {t.hero_subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="/_files/ugd/bbb9df_ac87e01cbc2f416ab03b45184d98ab77.pdf?v=13"
              data-testid="hero-explore-menu-btn"
              className="inline-flex items-center rounded-full bg-bergamote-600 text-cream-50 px-7 py-3.5 text-sm font-semibold hover:bg-bergamote-700 hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
            >
              {t.hero_cta_menu}
            </a>
            <a
              href="https://www.thefork.com/restaurant/la-bergamote-r752240"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-table-btn"
              className="inline-flex items-center rounded-full border border-bergamote-600/40 text-bergamote-800 px-7 py-3.5 text-sm font-semibold hover:bg-bergamote-100 hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
            >
              {t.hero_cta_book}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-bergamote-700"
          >
            <span className="inline-flex items-center gap-1.5" data-testid="hero-rating-badge">
              <Star className="w-4 h-4 fill-saffron-500 text-saffron-500" />
              <strong className="text-bergamote-900">{t.hero_rating}</strong>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-4 h-4" /> {t.contact_address}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-4 h-4" /> {t.hero_hours}
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="arch-frame overflow-hidden border border-bergamote-600/20 shadow-2xl shadow-bergamote-600/20 bg-cream-50">
              <motion.img
                src="/logo.png"
                alt="La Bergamote — hand-drawn façade of the salon on Rue d'Enfer"
                style={{ y: imgY }}
                className="w-full h-[520px] object-contain p-6"
                data-testid="hero-image"
              />
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        onClick={() => scrollToId("manifesto")}
        data-testid="hero-scroll-down"
        aria-label="Scroll down"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-bergamote-600 hover:text-saffron-600 transition-colors duration-300 animate-bounce"
      >
        <ArrowDown className="w-5 h-5" />
      </motion.button>
    </section>
  );
};
